import React from 'react'
import { useI18n } from '../../i18n'
import { Button } from '../ui/Button'
import type { OperationalControlStateRow } from '../../services/controlAssuranceApi'

type Props = {
  state: OperationalControlStateRow
  busy?: boolean
  onVerify?: (id: string) => void
}

function healthColor(health: string) {
  switch (health) {
    case 'HEALTHY':
      return 'text-emerald-700 bg-emerald-50 border-emerald-200'
    case 'WARNING':
      return 'text-amber-800 bg-amber-50 border-amber-200'
    case 'DEGRADED':
      return 'text-orange-800 bg-orange-50 border-orange-200'
    case 'FAILED':
      return 'text-red-800 bg-red-50 border-red-200'
    case 'RECOVERING':
      return 'text-blue-800 bg-blue-50 border-blue-200'
    default:
      return 'text-slate-600 bg-slate-50 border-slate-200'
  }
}

export default function ControlHealthCard({ state, busy, onVerify }: Props) {
  const { t } = useI18n()
  const summary =
    state.explanations?.summary ||
    t('controlAssurance.healthExplanation', 'Operational health evaluation available.')

  return (
    <article className={`rounded-lg border p-3 ${healthColor(state.controlHealth)}`}>
      <div className="flex flex-wrap justify-between gap-2">
        <div>
          <p className="text-xs font-semibold m-0">
            {state.canonicalControl?.code || t('controlAssurance.canonicalControl', 'Canonical control')}
          </p>
          <p className="text-[11px] opacity-80 m-0 mt-0.5">
            {t('controlAssurance.controlHealth', 'Control health')}: {state.controlHealth} ·{' '}
            {t('controlAssurance.assuranceLevel', 'Assurance level')}: {state.assuranceLevel}
          </p>
        </div>
        <span className="text-[10px] font-medium uppercase">
          {t('controlAssurance.operationalPressure', 'Pressure')}: {Math.round((state.operationalPressure || 0) * 100)}%
        </span>
      </div>
      <p className="text-xs mt-2 mb-0 opacity-90">{summary}</p>
      {state.informationSystem?.name ? (
        <p className="text-[11px] mt-1 mb-0 opacity-75">IS: {state.informationSystem.name}</p>
      ) : null}
      {onVerify ? (
        <Button type="button" size="small" color="outline" className="mt-2" disabled={busy} onClick={() => onVerify(state.id)}>
          {t('controlAssurance.continuousVerification', 'Continuous verification')}
        </Button>
      ) : null}
    </article>
  )
}
